/**
 * Sentry wiring for the LDG Hub.
 *
 * Captures unhandled errors, unhandled promise rejections, and React render
 * errors (via the Hub's own error boundaries), plus a masked screen recording
 * of the minute before an error, so we can see what the user did before
 * something blew up. The recorder loads after the page is up
 * (lib/sentryReplay.ts).
 *
 * THE SDK ITSELF LOADS AFTER THE PAGE IS UP TOO (speed plan 2026-10-19, P2).
 * This file no longer imports @sentry/react at the top: it is a small front
 * door that every caller uses, and the SDK arrives through a dynamic import
 * once the window has loaded and the phone is idle. Measured before this
 * change, the Sentry bundle was the single biggest piece of startup script
 * time on a throttled phone (1,263 ms in the 2026-10-03 audit) and about
 * 49 KB on the wire ahead of the first screen. Nothing is lost while it
 * loads: a report, a user tag or a breadcrumb made before then waits in a
 * short queue and is handed over the moment the SDK starts, and an error
 * thrown before then is caught by the two window listeners below and queued
 * the same way. The first error also starts the load at once rather than
 * waiting for idle, so a crash on a slow phone is not left sitting in the
 * queue while the person taps Reload.
 *
 * Failure modes intentionally degrade gracefully:
 *   - No VITE_SENTRY_DSN set: init silently skipped; the Hub still works
 *     fine, we just have no remote error capture. The /hub-check page
 *     surfaces this clearly so we never accidentally ship "Sentry off."
 *   - The SDK chunk fails to load, or Sentry init throws: status flips to
 *     'error' and /hub-check says so; the app keeps running.
 *
 * Sampling: ONE PAGE LOAD IN TEN is traced (Bobbie, 2026-10-19, "Yes, trim
 * to 1 in 10 now", DECISIONS 133 item 9; it was every page load before), and
 * a screen recording is kept only for a visit that hits an error
 * (DECISIONS 109). Errors are never sampled: every one is sent.
 */

// Type only: erased at build time, so it puts no Sentry code in startup.
import type * as SentrySdk from '@sentry/react';
// Explicit .ts extension (allowImportingTsExtensions in tsconfig.json): this
// file is imported directly by node:test under --experimental-strip-types
// for the setSentryUser contract test, and that loader will not resolve an
// extensionless relative specifier. Same rule as lib/alternates.ts.
import { SENTRY_RELEASE } from './buildInfo.ts';
// Imports nothing, so it costs startup almost nothing.
import { scrubBreadcrumb, scrubShareTokensDeep } from './sentryBreadcrumbs.ts';
import { isCompSharePath } from './sharePath.ts';
import { sentryEnvironmentFor } from './sentryEnvironment.ts';

type SentryApi = typeof SentrySdk;
type SeverityLevel = 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';
type CaptureContext = {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  contexts?: Record<string, Record<string, unknown>>;
};

export type SentryStatus =
  | { state: 'disabled'; reason: 'no-dsn' }
  | { state: 'starting'; environment: string; release?: string }
  | { state: 'enabled'; environment: string; release?: string }
  | { state: 'error'; message: string };

let status: SentryStatus = { state: 'disabled', reason: 'no-dsn' };

/** One page load in ten is traced (DECISIONS 133 item 9). */
export const SENTRY_TRACES_SAMPLE_RATE = 0.1;

/** The SDK once it has loaded and started; null until then. */
let sdk: SentryApi | null = null;
/**
 * A report still waiting for the SDK, in a shape that survives a page reload
 * (see CARRY_KEY). Only reports are carried; a user tag or a breadcrumb is
 * set again by the next page itself.
 */
type CarriedReport =
  | { kind: 'message'; message: string; level: SeverityLevel; tags?: Record<string, string> }
  | {
      kind: 'exception';
      name: string;
      message: string;
      stack?: string;
      tags?: Record<string, string>;
      componentStack?: string;
    };

/** Calls made before the SDK arrived, in order. Capped so a loop cannot grow it forever. */
const pending: Array<{ run: (sentry: SentryApi) => void; carry?: CarriedReport }> = [];
const PENDING_LIMIT = 100;
let startLoad: (() => void) | null = null;

/**
 * Run fn against the SDK now if it is up, or queue it for the moment it is.
 * Dropped when Sentry is off or broken, which is what the SDK itself does with
 * a call made before init. `urgent` is for reports: it starts the load now.
 * `carry` is the same report written plainly, for a page that goes away
 * before the SDK arrives (CARRY_KEY).
 */
function withSentry(fn: (sentry: SentryApi) => void, urgent = false, carry?: CarriedReport): void {
  if (sdk) {
    fn(sdk);
    return;
  }
  if (status.state !== 'starting') return;
  if (pending.length < PENDING_LIMIT) pending.push({ run: fn, carry });
  if (urgent && startLoad) startLoad();
}

/**
 * A REPORT MADE JUST BEFORE A RELOAD IS SENT BY THE NEXT PAGE (refute round,
 * 2026-10-25). Three paths report and reload in the same breath:
 * reloadGuard's 'stale-chunk reload', firestoreRecovery's 'firestore-closed
 * reload', and a crash where the person taps Reload in the first seconds.
 * Before the SDK moved out of startup it sent those before the page went
 * away; with the SDK still loading, the queue above went with the page. So
 * when the page goes away (pagehide) with reports still queued, they are
 * written to this tab's sessionStorage, and the next page load in the tab
 * queues them first, tagged sentry_carried:previous-page. A page kept in the
 * back/forward cache clears the copy when it comes back (pageshow), because
 * it still holds and sends the originals itself.
 *
 * PRIVACY: only what the report itself would send (its words, level, tags,
 * and for an error its name, message and stack), only in sessionStorage,
 * which this tab alone can read and which goes when the tab closes, and only
 * until the next page takes it.
 */
const CARRY_KEY = 'ldg-sentry-carry';
const CARRY_LIMIT = 10;
const CARRY_TEXT_LIMIT = 1000;
const CARRY_STACK_LIMIT = 8000;

function clip(text: string, limit: number): string {
  return text.length > limit ? text.slice(0, limit) : text;
}

function stringTags(tags: Record<string, string | undefined> | undefined): Record<string, string> | undefined {
  if (!tags) return undefined;
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(tags)) {
    if (typeof value === 'string') out[key] = clip(value, 200);
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function messageCarry(
  message: string,
  levelOrContext: SeverityLevel | { level: SeverityLevel; tags?: Record<string, string | undefined> }
): CarriedReport {
  return typeof levelOrContext === 'string'
    ? { kind: 'message', message: clip(message, CARRY_TEXT_LIMIT), level: levelOrContext }
    : {
        kind: 'message',
        message: clip(message, CARRY_TEXT_LIMIT),
        level: levelOrContext.level,
        tags: stringTags(levelOrContext.tags),
      };
}

function exceptionCarry(
  error: unknown,
  tags?: Record<string, string | undefined>,
  componentStack?: string
): CarriedReport {
  const isError = error instanceof Error;
  return {
    kind: 'exception',
    name: clip(isError ? error.name || 'Error' : 'Error', 200),
    message: clip(isError ? error.message : String(error), CARRY_TEXT_LIMIT),
    ...(isError && typeof error.stack === 'string' ? { stack: clip(error.stack, CARRY_STACK_LIMIT) } : {}),
    ...(stringTags(tags) ? { tags: stringTags(tags) } : {}),
    ...(componentStack ? { componentStack: clip(componentStack, CARRY_STACK_LIMIT) } : {}),
  };
}

/** Send one carried report, marked so it is not mistaken for this page. */
function replayCarried(report: CarriedReport): (sentry: SentryApi) => void {
  const tags = { ...(report.tags ?? {}), sentry_carried: 'previous-page' };
  if (report.kind === 'message') {
    return (Sentry) => Sentry.captureMessage(report.message, { level: report.level, tags });
  }
  return (Sentry) => {
    const error = new Error(report.message);
    error.name = report.name;
    if (report.stack) error.stack = report.stack;
    Sentry.captureException(error, {
      tags,
      ...(report.componentStack ? { contexts: { react: { componentStack: report.componentStack } } } : {}),
    });
  };
}

function isCarriedReport(value: unknown): value is CarriedReport {
  if (!value || typeof value !== 'object') return false;
  const report = value as Record<string, unknown>;
  if (typeof report.message !== 'string') return false;
  if (report.kind === 'message') return typeof report.level === 'string';
  return report.kind === 'exception' && typeof report.name === 'string';
}

/** Read and remove what the page before this one left. Never throws. */
function takeCarriedReports(): CarriedReport[] {
  try {
    const storage = window.sessionStorage;
    const raw = storage.getItem(CARRY_KEY);
    if (raw === null) return [];
    storage.removeItem(CARRY_KEY);
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isCarriedReport).slice(0, CARRY_LIMIT) : [];
  } catch {
    return [];
  }
}

/** pagehide: write down any report the SDK never got. Never throws. */
function carryPendingReports(): void {
  if (sdk) return;
  const reports = pending.flatMap((item) => (item.carry ? [item.carry] : [])).slice(0, CARRY_LIMIT);
  if (reports.length === 0) return;
  try {
    window.sessionStorage.setItem(CARRY_KEY, JSON.stringify(reports));
  } catch {
    // Storage blocked or full: the cost is these reports, as before.
  }
}

/** pageshow from the back/forward cache: this page still holds the originals. */
function dropCarriedCopy(event: PageTransitionEvent): void {
  if (!event.persisted) return;
  try {
    window.sessionStorage.removeItem(CARRY_KEY);
  } catch {
    // Nothing to do; the next page would send a duplicate at worst.
  }
}

/** How long to wait for an idle moment after the page loads before starting Sentry anyway. */
const SENTRY_IDLE_TIMEOUT_MS = 2000;
/** The latest Sentry waits, even if the window's load event never fires. */
const SENTRY_LATEST_START_MS = 15000;

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  // N14: never on the comp share page. Its address is a family's secret, and
  // Sentry reports the address with every event, trace and replay.
  if (typeof window !== 'undefined' && isCompSharePath(String(window.location?.pathname ?? ''))) {
    status = { state: 'disabled', reason: 'no-dsn' };
    return;
  }
  if (!dsn) {
    status = { state: 'disabled', reason: 'no-dsn' };
    return;
  }

  const environment = sentryEnvironmentFor(
    typeof window === 'undefined' ? undefined : window.location.hostname,
    import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined,
    import.meta.env.MODE,
  );
  const release = SENTRY_RELEASE;
  status = { state: 'starting', environment, release };

  // What the page before a reload never got to send goes first (CARRY_KEY).
  for (const report of takeCarriedReports()) withSentry(replayCarried(report), false, report);
  window.addEventListener('pagehide', carryPendingReports);
  window.addEventListener('pageshow', dropCarriedCopy);

  // Errors thrown before the SDK is up. Sentry's own handlers replace these
  // the moment it starts, so these come off first and nothing is sent twice.
  const onError = (event: ErrorEvent) => {
    const error = event.error ?? new Error(event.message || 'Script error');
    withSentry((Sentry) => Sentry.captureException(error, { mechanism: { handled: false, type: 'auto.browser.global_handlers.onerror' } }), true, exceptionCarry(error));
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    withSentry((Sentry) => Sentry.captureException(reason, { mechanism: { handled: false, type: 'auto.browser.global_handlers.onunhandledrejection' } }), true, exceptionCarry(reason));
  };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  let started = false;
  const load = () => {
    if (started) return;
    started = true;
    import('@sentry/react')
      .then((Sentry) => {
        window.removeEventListener('error', onError);
        window.removeEventListener('unhandledrejection', onRejection);
        try {
          Sentry.init({
            dsn,
            environment,
            release,
            // NO RECORDER HERE. It is added after the page is up, by
            // loadErrorReplayWhenIdle() below, so its code is not part of startup.
            integrations: [Sentry.browserTracingIntegration()],
            tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
            // Only a visit that hits an error is recorded. Bobbie ended the
            // every-visit usage recording on 2026-10-03 ("End it now
            // (Recommended)", DECISIONS 109), four days before the end date
            // DECISIONS 43 had set. The recorder reads both rates from here when
            // it is added later, and masks every word, input, photo and video.
            replaysSessionSampleRate: 0,
            replaysOnErrorSampleRate: 1.0,
            sendDefaultPii: false,
            beforeBreadcrumb: scrubBreadcrumb,
            // N14 second lock: a share page address that reached a report
            // anyway (a tab that started elsewhere and navigated to /s/)
            // leaves as /s/:token. sentryBreadcrumbs.ts says why.
            beforeSend: (event) => scrubShareTokensDeep(event),
            beforeSendTransaction: (event) => scrubShareTokensDeep(event),
          });
        } catch (err) {
          status = {
            state: 'error',
            message: err instanceof Error ? err.message : String(err),
          };
          pending.length = 0;
          return;
        }
        sdk = Sentry;
        status = { state: 'enabled', environment, release };
        for (const item of pending.splice(0)) {
          try {
            item.run(Sentry);
          } catch {
            // One bad queued call must not stop the rest.
          }
        }
        loadErrorReplayWhenIdle();
      })
      .catch((err: unknown) => {
        // Usually a deploy that replaced the hashed chunk, or no signal. The
        // cost is this visit's reports. It must never reach lib/lazyRoute.ts's
        // stale-chunk reload, which is for pages, not for this.
        status = {
          state: 'error',
          message: `Sentry could not load: ${err instanceof Error ? err.message : String(err)}`,
        };
        pending.length = 0;
      });
  };
  startLoad = load;
  afterPageLoadAndIdle(load, SENTRY_IDLE_TIMEOUT_MS, SENTRY_IDLE_TIMEOUT_MS);
  setTimeout(load, SENTRY_LATEST_START_MS);
}

/**
 * Wait for the window's load event, then for an idle moment (at most
 * idleTimeoutMs), or fallbackDelayMs where the browser cannot report idle.
 */
function afterPageLoadAndIdle(run: () => void, idleTimeoutMs: number, fallbackDelayMs: number): void {
  const afterLoad = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: idleTimeoutMs });
    } else {
      setTimeout(run, fallbackDelayMs);
    }
  };
  if (document.readyState === 'complete') afterLoad();
  else window.addEventListener('load', afterLoad, { once: true });
}

/** How long to wait for an idle moment before loading the recorder anyway. */
const ERROR_REPLAY_IDLE_TIMEOUT_MS = 5000;
/** A browser without requestIdleCallback waits this long after load instead. */
const ERROR_REPLAY_FALLBACK_DELAY_MS = 2000;

/**
 * THE RECORDER LOADS AFTER THE PAGE IS UP (Bobbie, 2026-10-03, DECISIONS
 * 109). It waits for the window's load event and then for an idle moment
 * (or ERROR_REPLAY_FALLBACK_DELAY_MS where the browser cannot report idle),
 * so the first screen, the sign-in and the first reads never share the phone
 * with it.
 *
 * A failed load is dropped on purpose. The usual cause is a deploy that
 * replaced the hashed chunk while this tab was open, and the only cost is
 * that this visit's errors arrive without a recording. It must never reach
 * lib/lazyRoute.ts's stale-chunk reload, which is for pages, not for this.
 */
function loadErrorReplayWhenIdle(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const load = () => {
    import('./sentryReplay.ts')
      .then((recorder) => recorder.startErrorReplay())
      .catch(() => undefined);
  };
  const afterLoad = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(load, { timeout: ERROR_REPLAY_IDLE_TIMEOUT_MS });
    } else {
      setTimeout(load, ERROR_REPLAY_FALLBACK_DELAY_MS);
    }
  };
  if (document.readyState === 'complete') afterLoad();
  else window.addEventListener('load', afterLoad, { once: true });
}

export function getSentryStatus(): SentryStatus {
  return status;
}

/**
 * Helper for /hub-check's "send a test error" button. Throws on the Sentry
 * client so we can verify capture end-to-end. Returns whether the SDK was
 * available to receive it (vs. silently no-oping because init was skipped).
 */
export function sendSentryTestError(): boolean {
  if (status.state !== 'enabled' && status.state !== 'starting') return false;
  withSentry(
    (Sentry) => Sentry.captureMessage('LDG Hub /hub-check test error', 'error'),
    true,
    messageCarry('LDG Hub /hub-check test error', 'error')
  );
  return true;
}

/**
 * One Sentry message when the silent start-up push refresh fails on a device
 * that WAS set up for alerts (feedback r2iDin5L; Bobbie, 2026-10-19: "can we
 * use sentry to fix?"). Search Sentry for push_refresh_reason to find them.
 *
 * PRIVACY: the only new thing sent is the reason, a fixed code from
 * PushFailureReason (for example push-token-rejected). The person shows up
 * through the uid and role setSentryUser already put on the scope, and
 * nothing else: never the push token, never the raw error text. The caller
 * (PushSync) sends at most one per day per device, and only once the
 * account's own doc has loaded, so the scope carries that uid and role.
 * Sent while Sentry is still starting too: it waits in the queue behind the
 * setSentryUser call, so it still carries that uid and role.
 */
export function reportPushRefreshFailure(reason: string): boolean {
  if (status.state !== 'enabled' && status.state !== 'starting') return false;
  const message = `push refresh failed: ${reason}`;
  const context = { level: 'warning' as const, tags: { push_refresh_reason: reason } };
  withSentry((Sentry) => Sentry.captureMessage(message, context), false, messageCarry(message, context));
  return true;
}

/**
 * One Sentry message for every upload that fails (2026-10-22: a parent's
 * failed video left no trace anywhere, because every upload error is caught
 * and shown on screen). main.tsx hands this to the upload manager. Search
 * Sentry for upload_stage or upload_code to find them.
 *
 * A failure while Sentry is still loading waits in the queue and starts the
 * load at once, so an upload that fails in the first seconds of a visit is
 * still reported, behind the setSentryUser call that tags who it was.
 *
 * PRIVACY: the step, a fixed code, the HTTP status, the surface, the size in
 * MB and the file type. Never the file name or the error's own words. The
 * person shows up only through the uid and role setSentryUser already put
 * on the scope.
 */
export function reportUploadFailureToSentry(failure: {
  stage: string;
  code: string;
  status?: number;
  surface?: string;
  totalBytes?: number;
  contentType?: string;
}): boolean {
  if (status.state !== 'enabled' && status.state !== 'starting') return false;
  const contentType = failure.contentType && /^[a-z]+\/[a-z0-9.+-]{1,64}$/i.test(failure.contentType)
    ? failure.contentType.toLowerCase()
    : failure.contentType ? 'other' : 'unknown';
  withSentry((Sentry) => Sentry.captureMessage(`upload failed: ${failure.stage} ${failure.code}`, {
    level: 'warning',
    tags: {
      upload_stage: failure.stage,
      upload_code: failure.code,
      upload_surface: failure.surface ?? 'attachment',
      upload_type: contentType,
    },
    contexts: {
      upload: {
        stage: failure.stage,
        code: failure.code,
        ...(failure.status ? { status: failure.status } : {}),
        ...(failure.totalBytes ? { sizeMb: Math.round((failure.totalBytes / (1024 * 1024)) * 10) / 10 } : {}),
        contentType,
      },
    },
    fingerprint: ['upload-failure', failure.stage, failure.code],
  }), true, messageCarry(`upload failed: ${failure.stage} ${failure.code}`, {
    level: 'warning',
    tags: {
      upload_stage: failure.stage,
      upload_code: failure.code,
      upload_surface: failure.surface ?? 'attachment',
      upload_type: contentType,
    },
  }));
  return true;
}

export function reportErrorBoundaryIssue(
  error: unknown,
  componentStack?: string
): boolean {
  if (status.state !== 'enabled' && status.state !== 'starting') return false;
  const reportError = error instanceof Error ? error : new Error(String(error));
  withSentry((Sentry) => Sentry.captureException(reportError, {
    tags: { source: 'app-error-boundary-submit' },
    // WHY THE COMPONENT STACK RIDES ALONG. A production build reports a React
    // invariant as a bare number ("Minified React error #310"), which names no
    // component, no file and no route. Without this, an issue in Sentry says
    // only that something broke somewhere. The stack is what turns it into a
    // place to look. Sentry's own ErrorBoundary already computes it.
    //
    // THIS FUNCTION NEVER RECEIVED IT, and an earlier comment here said it did.
    // Verified against origin/hub-staging: the signature was
    // reportErrorBoundaryIssue(error: unknown) and the call site passed only
    // error. The stack was dropped UPSTREAM, by the Fallback destructure in
    // AppErrorBoundary.tsx. Blaming this file sent a reader to the wrong place.
    ...(componentStack ? { contexts: { react: { componentStack } } } : {}),
  }), true, exceptionCarry(reportError, { source: 'app-error-boundary-submit' }, componentStack));
  return true;
}

/**
 * Tags every later Sentry event with WHO hit it, so an issue's "Users"
 * count finally means something (every issue used to show 0, which made a
 * single unlucky account indistinguishable from ten different families all
 * hitting the same bug).
 *
 * PRIVACY IS THE CONSTRAINT: uid and role, and NOTHING else - no email, no
 * display name, no dancer name or id, ever. The Hub already masks all
 * on-screen text and every input in session replay for the identical reason
 * (maskAllText / maskAllInputs in lib/sentryReplay.ts); this is the same
 * rule applied to the user-context field instead of the recording. A
 * role-shaped bug ("only family accounts see this") becomes visible in the
 * issue list this way without Sentry ever holding a second directory of who
 * signed in.
 *
 * Not gated behind getSentryStatus() the way sendSentryTestError() is: a
 * call made before the SDK has loaded waits in the queue and is applied, in
 * order, the moment it starts (so a sign-in followed quickly by a sign-out
 * still ends with no user), and a call made with Sentry off is dropped. It
 * has no boolean result to report back the way the /hub-check button does.
 */
export function setSentryUser(uid: string, role: string): void {
  withSentry((Sentry) => {
    Sentry.setUser({ id: uid, role });
    // The same role as a TAG, because Sentry's issue and replay search filter
    // on tags ("role:parent"), which is how reports get split by who was using
    // the app (Bobbie, 2026-09-23). Still the role only.
    Sentry.setTag('role', role);
  });
}

/**
 * Sign-out (and "no signed-in user yet"): never let a report from the NEXT
 * person on this device get attributed to whoever was signed in a moment
 * ago. Pass null to Sentry.setUser to clear the field entirely rather than
 * setting empty-string placeholders, which would still read as "a user."
 */
export function clearSentryUser(): void {
  withSentry((Sentry) => {
    Sentry.setUser(null);
    Sentry.setTag('role', undefined);
  });
}

/**
 * Mark this tab as a DANCER'S OWN session, younger or older, so reports from
 * kids' sessions can be found on their own ("dancer_mode:younger").
 * An ordinary dancer sign-in has no Firebase account, so setSentryUser
 * never runs for it and nothing else says a kid is using the app. A
 * promoted Jr Staff dancer is the exception: she holds a dancer cookie AND
 * a staff account and her home is still the dancer page, so her tab carries
 * both her role and this tag. That is intended: she is one person however
 * she signs in (CLAUDE.md), and on her dancer page she is using the kid's
 * view. PRIVACY: this tag is the mode only, never the dancer's name or
 * student id. Never called for a staff preview of a dancer's page
 * (DancerHome checks), which is a grown-up, not a kid.
 */
export function tagSentryDancerSession(mode: 'younger' | 'older'): void {
  withSentry((Sentry) => Sentry.setTag('dancer_mode', mode));
}

/** Dancer sign-out, and leaving the dancer page: the next visitor is not a kid. */
export function clearSentryDancerSession(): void {
  withSentry((Sentry) => Sentry.setTag('dancer_mode', undefined));
}

/**
 * Report an error the Hub caught and carried on from. Queued until the SDK is
 * up, and starts the load at once, so it is never lost to the wait.
 */
export function captureSentryException(error: unknown, context?: CaptureContext): void {
  const componentStack = context?.contexts?.react?.componentStack;
  withSentry(
    (Sentry) => Sentry.captureException(error, context),
    true,
    exceptionCarry(error, context?.tags, typeof componentStack === 'string' ? componentStack : undefined)
  );
}

/** Send a one-line note such as 'stale-chunk reload', with a level or a level and tags. */
export function captureSentryMessage(
  message: string,
  levelOrContext: SeverityLevel | { level: SeverityLevel; tags?: Record<string, string | undefined> }
): void {
  withSentry((Sentry) => Sentry.captureMessage(message, levelOrContext), true, messageCarry(message, levelOrContext));
}

/** A breadcrumb on the scope, so the next report shows the step that led to it. */
export function addSentryBreadcrumb(crumb: {
  category: string;
  level: SeverityLevel;
  message: string;
  data?: Record<string, string>;
}): void {
  withSentry((Sentry) => Sentry.addBreadcrumb(crumb));
}

/**
 * A React render crash, reported the way Sentry's own ErrorBoundary reports
 * one (captureReactException with React's component stack, marked handled
 * because the Hub shows its own crash screen). AppErrorBoundary calls this;
 * it used to wrap Sentry.ErrorBoundary, which put the SDK into startup.
 */
export function captureSentryReactCrash(error: unknown, componentStack: string | undefined): void {
  withSentry(
    (Sentry) =>
      Sentry.captureReactException(error, { componentStack: componentStack ?? '' }, {
        mechanism: { handled: true, type: 'auto.function.react.error_boundary' },
      }),
    true,
    exceptionCarry(error, undefined, componentStack)
  );
}
